import { toast } from 'react-toastify';
import routes from '../routes';

type DownloadParams = { [key: string]: string | number | boolean };

// `filename*=UTF-8''report%20final.xlsx` (RFC 5987) wins over a plain `filename`
const ENCODED_FILE_NAME = /filename\*=[^']*'[^']*'([^;]+)/i;
const PLAIN_FILE_NAME = /filename=\s*(?:"([^"]*)"|([^;]+))/i;
// Spring writes the plain `filename` as an RFC 2047 encoded word when a charset is given;
// RFC 6266 forbids decoding those in HTTP, so such a value counts as unreadable
const ENCODED_WORD = /^=\?.*\?=$/;
const HTTP_NO_CONTENT = 204;

/**
 * Reads the file name out of a `Content-Disposition` header.
 * The header may also carry a `name` field (Spring's `setContentDispositionFormData`
 * writes `form-data; name="attachment"; filename="…"`), so the field has to be
 * matched by its key and never by its position.
 */
export const parseAttachmentFileName = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) {
    return null;
  }
  const encoded = ENCODED_FILE_NAME.exec(contentDisposition);
  if (encoded) {
    const value = encoded[1].trim();
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  const plain = PLAIN_FILE_NAME.exec(contentDisposition);
  if (!plain) {
    return null;
  }
  const value = plain[1] ?? plain[2] ?? '';
  const fileName = value.trim();
  if (fileName.length === 0 || ENCODED_WORD.test(fileName)) {
    return null;
  }
  return fileName;
};

const buildRequestUrl = (url: string, params?: DownloadParams) => {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }
  const paramsArray = Object.entries(params).map(([key, value]) => [key, value.toString()]);
  const query = new URLSearchParams(paramsArray).toString();
  return `${url}?${query}`;
};

const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const downloadAsFile = async (
  requestUrl: string,
  fallbackFileName: string,
  forcedType?: string,
) => {
  const accessToken = JSON.parse(localStorage?.getItem('banditto') as string)?.accessToken;
  const response = await fetch(requestUrl, {
    method: 'GET',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  // a missing file may come back as 204 instead of 404, and `ok` covers 204, so an empty answer
  // has to be rejected here or a 0-byte file would be saved silently
  if (!response.ok || response.status === HTTP_NO_CONTENT) {
    // without this the error body itself would be saved as the requested file
    toast.error('Unable to download the file');
    return;
  }
  const data = await response.blob();
  if (data.size === 0) {
    toast.error('Unable to download the file');
    return;
  }
  const headerFileName = parseAttachmentFileName(response.headers.get('Content-Disposition'));
  const contentType = response.headers.get('Content-Type');
  const blob = new Blob([data], { type: forcedType ?? contentType ?? 'application/octet-stream' });
  saveBlob(blob, headerFileName ?? fallbackFileName);
};

export const convertBinaryToLink = (
  url: string,
  params?: DownloadParams,
  fallbackFileName = 'download',
) => {
  const requestUrl = buildRequestUrl(url, params);
  return downloadAsFile(requestUrl, fallbackFileName);
};

export const downloadPDFFile = (url: string, fallbackFileName = 'invoice.pdf') => {
  const requestUrl = `${routes.api.basePath()}${url}`;
  return downloadAsFile(requestUrl, fallbackFileName, 'application/pdf');
};
